import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Sparkles, FlaskConical, Save, X, Plus, Clock, Play } from "lucide-react";
import { VerificationBadges } from "@/components/VerificationBadges";

const LIST_FIELDS = [
  { key: "industries", label: "Target industries / segments", hint: "Who buys — as market segments" },
  { key: "searchTerms", label: "Buyer vocabulary (search terms)", hint: "Short phrases the pipeline searches with" },
  { key: "personas", label: "Buyer personas (job titles)", hint: "Exact titles of decision-makers" },
  { key: "geos", label: "Geographies", hint: "Countries, regions or states" },
  { key: "entityTypes", label: "Entity types", hint: "company · fund · practice · nonprofit · agency · facility" },
  { key: "regulatoryContext", label: "Registries / regulators", hint: "Drives which public registries the planner uses" },
  { key: "signals", label: "Buying signals", hint: "Triggers worth acting on" },
  { key: "negatives", label: "Exclusions", hint: "Segments or companies to never surface" },
  { key: "lookalikeSeeds", label: "Look-alike seeds", hint: "Current customers to model" },
];

const ChipList = ({ field, values, onChange }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`spec-field-${field.key}`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm text-white font-medium">{field.label}</span>
        <span className="text-[11px] text-gray-500">{field.hint}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-ism-teal/10 border border-ism-teal/30 text-ism-teal text-xs px-2 py-0.5">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-white" data-testid={`spec-remove-${field.key}-${v}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {!values.length && <span className="text-xs text-gray-600 italic">none</span>}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Add…" className="h-8 text-xs bg-black/30 border-white/10" data-testid={`spec-input-${field.key}`} />
        <Button size="sm" variant="outline" onClick={add} className="h-8 px-2 border-white/10" data-testid={`spec-add-${field.key}`}><Plus className="h-3 w-3" /></Button>
      </div>
    </div>
  );
};

const slug = (v) => v.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const ParkedIndustries = ({ spec, setSpec }) => {
  const parked = spec.parkedIndustries || [];
  const activate = (v) => setSpec({ ...spec, industries: [...(spec.industries || []), v], parkedIndustries: parked.filter((x) => x !== v) });
  const park = (v) => setSpec({ ...spec, industries: (spec.industries || []).filter((x) => x !== v), parkedIndustries: [...parked, v] });
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3" data-testid="spec-field-parkedIndustries">
      <div className="mb-2">
        <span className="text-sm text-white font-medium flex items-center gap-1.5 whitespace-nowrap"><Clock className="h-3.5 w-3.5 text-amber-300" /> Parked for later</span>
        <span className="block text-[11px] text-gray-500 mt-0.5">Not searched; the hunt guidance offers them when results run thin</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {parked.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] border border-white/10 text-gray-300 text-xs px-2 py-0.5" data-testid={`spec-parked-${slug(v)}`}>
            {v}
            <button onClick={() => activate(v)} className="text-ism-teal hover:text-white ml-1 inline-flex items-center gap-0.5" title="Hunt this industry now" data-testid={`spec-activate-${slug(v)}`}><Play className="h-3 w-3" /> activate</button>
          </span>
        ))}
        {!parked.length && <span className="text-xs text-gray-600 italic">none — everything in "Target industries" is hunted</span>}
      </div>
      {(spec.industries || []).length > 1 && (
        <p className="text-[11px] text-gray-500">Park an active one: {(spec.industries || []).map((v) => (
          <button key={v} onClick={() => park(v)} className="underline-offset-2 hover:underline text-gray-400 hover:text-amber-300 mr-2" data-testid={`spec-park-${slug(v)}`}>{v}</button>
        ))}</p>
      )}
    </div>
  );
};

export const TargetSpecEditor = ({ userId }) => {
  const [spec, setSpec] = useState(null);
  const [icp, setIcp] = useState("");
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/prospecting/targetspec/${userId}`)
      .then((r) => { setSpec(r.data.spec); setIcp(r.data.icp || ""); setStale(!!r.data.stale); })
      .catch(() => toast.error("Could not load targeting spec")).finally(() => setLoading(false));
  }, [userId]);

  const compile = async () => {
    setBusy("compile");
    try {
      const r = await axios.post(`${API}/prospecting/targetspec/${userId}/compile`, { icp });
      setSpec(r.data.spec);
      setStale(false);
      toast.success("ICP compiled from your description and profile");
    } catch { toast.error("Compile failed"); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const r = await axios.put(`${API}/prospecting/targetspec/${userId}`, spec);
      setSpec(r.data.spec);
      setStale(false);
      toast.success("Targeting spec saved — Hunt and the Stream now use it");
    } catch { toast.error("Save failed"); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const r = await axios.post(`${API}/prospecting/targetspec/${userId}/test`, spec);
      setTestResult(r.data);
    } catch { toast.error("Test run failed"); } finally { setBusy(""); }
  };
  const setSize = (key, raw) => {
    const n = raw === "" ? null : Number(String(raw).replace(/[^0-9.]/g, ""));
    setSpec({ ...spec, size: { ...(spec.size || {}), [key]: n === null || Number.isNaN(n) ? null : (key === "minRevenueUSD" ? n * 1e6 : n) } });
  };

  if (loading) return <div className="text-gray-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading targeting spec…</div>;
  if (!spec) return null;
  const size = spec.size || {};

  return (
    <div className="space-y-4" data-testid="targetspec-editor">
      <p className="text-xs text-gray-400">
        This is the single source of truth for who the agents hunt: onboarding, Hunt, the Autonomous Stream and the AI judge all read from here.
      </p>
      {stale && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 flex items-center gap-2" data-testid="spec-stale-banner">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />Your profile changed since this spec was hand-edited — review it or recompile.
        </div>
      )}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="spec-field-icp">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm text-white font-medium">Describe your ideal customer</span>
          <span className="text-[11px] text-gray-500">Plain language — compiled into the structured fields below</span>
        </div>
        <Textarea value={icp} onChange={(e) => setIcp(e.target.value)} data-testid="spec-icp-input"
          placeholder="e.g. Fortune 500 enterprises in financial services and healthcare, 5,000+ employees, deploying agentic AI"
          className="bg-white/5 border-white/10 text-white text-sm min-h-[70px]" />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-300">{spec.summary || "No summary yet — compile from your profile."}</p>
          <p className="text-[11px] text-gray-500 mt-1">
            Compiled by <span className="text-gray-300">{spec.compiledBy}</span> · confidence {(spec.confidence * 100).toFixed(0)}% · v{spec.version}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={compile} disabled={!!busy} className="border-white/10 text-gray-200" data-testid="spec-compile-btn">
            {busy === "compile" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}Recompile from description
          </Button>
          <Button size="sm" variant="outline" onClick={test} disabled={!!busy} className="border-white/10 text-gray-200" data-testid="spec-test-btn">
            {busy === "test" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FlaskConical className="h-3 w-3 mr-1" />}Test this ICP
          </Button>
          <Button size="sm" onClick={save} disabled={!!busy} className="bg-ism-teal hover:bg-ism-teal/90 text-white" data-testid="spec-save-btn">
            {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-ism-teal/30 bg-ism-teal/5 p-3" data-testid="spec-field-size">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm text-white font-medium">Company size gate</span>
          <span className="text-[11px] text-gray-500">Hard filter — firms outside the range (±1 size bracket) are dropped before people lookup</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[["minEmployees", "Min employees", size.minEmployees], ["maxEmployees", "Max employees", size.maxEmployees],
            ["minRevenueUSD", "Min revenue ($M)", size.minRevenueUSD ? size.minRevenueUSD / 1e6 : ""]].map(([key, label, val]) => (
            <label key={key} className="text-[11px] text-gray-400">
              {label}
              <Input type="number" min="0" value={val ?? ""} onChange={(e) => setSize(key, e.target.value)} placeholder="any"
                className="mt-1 bg-white/5 border-white/10 text-white text-sm h-8" data-testid={`spec-size-${key}`} />
            </label>
          ))}
          <label className="text-[11px] text-gray-400">
            Breadth
            <select value={spec.breadth || "balanced"} onChange={(e) => setSpec({ ...spec, breadth: e.target.value })} data-testid="spec-breadth-select"
              className="mt-1 w-full h-8 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2">
              <option value="focused">Focused — one vertical</option>
              <option value="balanced">Balanced — adjacent segments</option>
              <option value="exploratory">Exploratory — any vertical</option>
            </select>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {LIST_FIELDS.map((f) => (
          <ChipList key={f.key} field={f} values={spec[f.key] || []} onChange={(vals) => setSpec({ ...spec, [f.key]: vals })} />
        ))}
        <ParkedIndustries spec={spec} setSpec={setSpec} />
      </div>

      {testResult && (
        <div className="rounded-xl border border-ism-teal/30 bg-ism-teal/5 p-3" data-testid="spec-test-results">
          <p className="text-xs text-ism-teal uppercase tracking-wider mb-2">
            Test run · {testResult.run?.searchProvider} · {testResult.run?.companies || 0} verified firms · {testResult.run?.raw} results screened → {testResult.prospects?.length || 0} passed the gate
            {testResult.run?.judged ? ` · ${testResult.run.judged} AI-judged` : ""}{testResult.run?.inferredBuyers ? ` · ${testResult.run.inferredBuyers} buyer roles inferred` : ""}
          </p>
          {testResult.needsICP && <p className="text-sm text-amber-300">{testResult.message}</p>}
          <div className="space-y-2">
            {(testResult.prospects || []).map((p, i) => (
              <div key={p.url || i} className="rounded-lg bg-black/30 border border-white/10 p-2" data-testid={`spec-test-prospect-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">{p.personName || p.companyName}</span>
                  <span className="text-xs text-ism-teal">{p.icpMatch?.score}% fit</span>
                </div>
                <p className="text-xs text-gray-400">
                  {p.personName ? `${p.personTitle}${p.companyName ? ` at ${p.companyName}` : ""}` : (p.inferredBuyer ? `Likely buyer: ${p.inferredBuyer.title} (AI-inferred, not verified)` : (p.companyFacts || [])[0])}
                </p>
                <VerificationBadges prospect={p} idx={`test-${i}`} />
                <p className="text-[11px] text-gray-500">{(p.whyGoodFit || [])[0]}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
